import type { State } from '../State';
import { clamp } from '../utils';
import { duration } from '../utils/duration';
import { getJSTDaysDiff } from '../utils/time';
import { config, PromoID } from './promo.ruleset';

/**
 * Check if promo should be shown
 * If cooldown >= 1 day we will use JST days, otherwise we use simple duration without JST conversation
 */
export function isPromoReady(state: State, id: PromoID, now: number): boolean {
  const promo = config[id];
  const isPromoActive = state.promo[id]?.active ?? true;
  if (!isPromoActive) {
    return false;
  }

  const { cooldown: promoCooldown, limit = 1 } = promo;

  const promoShownCounter = getPromoShownCounter(state, id);

  // If cooldown is an array, get cooldown for current count.
  const cooldown = Array.isArray(promoCooldown)
    ? promoCooldown[clamp(promoShownCounter - 1, 0, promoCooldown.length - 1)]
    : promoCooldown;

  if (promoShownCounter % limit !== 0) {
    // Haven't hit limit for this cooldown period
    return true;
  }

  const promoWasShownAt = state.promo[id]?.shownAt ?? 0;
  // If cooldown >= 1 day, we use JST days to calculate cooldown interval
  const useJstDays = cooldown >= duration({ days: 1 });
  const days = useJstDays ? cooldown / duration({ days: 1 }) : 0;
  const jstDaysDiff = useJstDays ? getJSTDaysDiff(now, promoWasShownAt) : 0;

  // When we should start showing promo timestamp
  const startAt = promo.start ?? now;
  // Is there end promo date
  const endPromoAt = promo.end ?? Infinity;

  // If cooldown >= 1 day, we use JST days to calculate cooldown interval
  const isCooldownReady = useJstDays
    ? jstDaysDiff >= days // In JST days
    : promoWasShownAt + cooldown <= now; // Simple duration interval

  // Optional conditions
  const isPromoStarted = startAt <= now;
  const isPromoNotFinished = endPromoAt >= now;

  return isCooldownReady && isPromoStarted && isPromoNotFinished;
}
/**
 * How many time promo was shown
 */
export function getPromoShownCounter(state: State, id: PromoID): number {
  return state.promo[id]?.count ?? 0;
}

export const getPromoRewards = (state: State, id: PromoID, now: number) =>
  config[id]?.getRewards?.(state, now);

/**
 * Check if promo rewards have been claimed
 */
export const hasClaimedPromoRewards = (state: State, id: PromoID) =>
  state.promo[id]?.claimedRewards ?? false;

/**
 * Check if can claim promo rewards
 */
export function canClaimPromoRewards(
  state: State,
  id: PromoID,
  now: number,
): boolean {
  // Config not found. This can happen if promo is over and the config was removed.
  if (!config[id]) return false;

  // Promo doesn't have rewards or they can't be claimed.
  if (!getPromoRewards(state, id, now)) return false;

  // Allow unclaimed rewards to be claimed
  const promo = state.promo[id];
  if (promo) {
    return !promo.claimedRewards;
  }

  // Otherwise rewards can be claimed if promo is ready
  return isPromoReady(state, id, now);
}
